import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  Loader2,
  MoreHorizontal,
  User,
  UserCircle,
  Zap,
  LayoutGrid,
} from 'lucide-react';
import { API } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import Fuse from 'fuse.js';
import getUserAvatar from '../../../application/common/utils/getUserAvatar';
import getSentenceInitials from '../../../application/common/utils/getSentenceInitials';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import featureFlag from '../../../application/common/featureFlag';
import {
  loadUsersAction,
  selectAllUsers,
  selectSearchString,
  selectFilters,
  setSearchStringAction,
  setFiltersAction,
  removeUsersAction,
  removeProfilesAction,
} from '../../../application/store/reducers/users';
import useOrgStore from '../../../application/store/zustand/useOrgStore';
import {
  getSeatInfo,
  getProrationPreview,
} from '../../../application/routes/settings/components/PaymentMethods/paymentApi';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { useToast } from '../../components/ui/toast';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

interface UserRow {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  title: string;
  status: string;
  avatar: string;
  initials: string;
  createdAt: string;
  memberType: string;
  functionalRoles: string;
  orgs: { items: { memberType: string; orgID: string }[] };
}

const searchUsersQuery = gql`
  query MyQuery($orgID: ID!, $searchString: String!, $nextToken: String) {
    searchUsers(
      filter: {
        name: { wildcard: $searchString }
        orgID: { eq: $orgID }
        status: { ne: "deleted" }
      }
      limit: 750
      sort: { direction: asc, field: name }
      nextToken: $nextToken
    ) {
      items {
        id
        email
        imageName
        imageType
        initials
        name
        functionalRoles
        phoneNumber
        role
        status
        title
        createdAt
        orgs {
          items {
            memberType
            orgID
          }
        }
      }
      nextToken
      total
    }
  }
`;

export function PeoplePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();

  const reduxUsers = useSelector(selectAllUsers);
  const reduxSearchString = useSelector(selectSearchString);
  const reduxFilters = useSelector(selectFilters);

  const reduxUsersArr = useMemo(() => {
    const arr = Object.values(reduxUsers) as unknown as UserRow[];
    arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return arr;
  }, [reduxUsers]);

  const [users, setUsers] = useState<UserRow[]>(reduxUsersArr);
  const [searchString, setSearchString] = useState(reduxSearchString || '');
  const [roleFilter, setRoleFilter] = useState(reduxFilters?.role || 'all');
  const [statusFilter, setStatusFilter] = useState(
    reduxFilters?.status || 'all'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [seatCheckLoading, setSeatCheckLoading] = useState(false);
  const [seatDialogOpen, setSeatDialogOpen] = useState(false);
  const [seatDialogTitle, setSeatDialogTitle] = useState('');
  const [seatDialogMessage, setSeatDialogMessage] = useState('');
  const [seatDialogConfirmText, setSeatDialogConfirmText] =
    useState('Continue');
  const [seatDialogAction, setSeatDialogAction] = useState<
    'proceed' | 'purchase' | null
  >(null);
  const toast = useToast();

  // Fetch users
  useEffect(() => {
    if (reduxUsersArr.length > 0) {
      setUsers(reduxUsersArr);
      return;
    }
    fetchPeople();
  }, []);

  useEffect(() => {
    setUsers(reduxUsersArr);
  }, [reduxUsersArr]);

  const fetchPeople = async () => {
    setIsLoading(true);
    try {
      let usersList: any[] = [];
      let nextToken: string | null = null;

      do {
        const res: any = await API.graphql({
          query: searchUsersQuery,
          variables: {
            orgID: global.me.orgID,
            searchString: '*',
            nextToken,
          },
        });
        usersList = [...usersList, ...(res?.data?.searchUsers?.items || [])];
        nextToken = res?.data?.searchUsers?.nextToken;
      } while (nextToken);

      const usersWithAvatar = await Promise.all(
        usersList.map(async (u) => {
          const avatar = await getUserAvatar(u.imageName, u.imageType, true);
          const initials = u.initials || getSentenceInitials(u.name);
          return { ...u, avatar, initials };
        })
      );

      setUsers(usersWithAvatar);
      dispatch(loadUsersAction(usersWithAvatar));
    } catch (err) {
      console.error('Error fetching people:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side search with Fuse.js
  const fuse = useMemo(
    () =>
      new Fuse(users, {
        keys: ['name', 'email', 'title', 'phoneNumber'],
        includeScore: true,
        threshold: 0.2,
      }),
    [users]
  );

  const filteredUsers = useMemo(() => {
    let list = users;

    // Apply search
    if (searchString.trim()) {
      const results = fuse.search(searchString);
      list = results.map((r) => r.item);
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      list = list.filter((u) => u.orgs?.items?.[0]?.memberType === roleFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      list = list.filter((u) => u.status === statusFilter);
    }

    return list;
  }, [users, searchString, roleFilter, statusFilter, fuse]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchString(value);
      dispatch(setSearchStringAction(value));
    },
    [dispatch]
  );

  const handleRoleChange = useCallback(
    (value: string) => {
      setRoleFilter(value);
      dispatch(setFiltersAction({ ...reduxFilters, role: value }));
    },
    [dispatch, reduxFilters]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      setStatusFilter(value);
      dispatch(setFiltersAction({ ...reduxFilters, status: value }));
    },
    [dispatch, reduxFilters]
  );

  const handleAddPerson = async () => {
    // fixed_rate and legacy orgs skip seat checks entirely
    const orgBillingType = useOrgStore.getState()?.orgData?.billingType;
    if (orgBillingType !== 'seat_based') {
      navigate('/v2/people/new');
      return;
    }

    setSeatCheckLoading(true);

    // Timeout fallback: if seat check takes too long, let them through
    const timeout = setTimeout(() => {
      setSeatCheckLoading(false);
      navigate('/v2/people/new');
    }, 10000);

    try {
      const seatRes = await getSeatInfo(global.me.orgID);
      clearTimeout(timeout);
      setSeatCheckLoading(false);

      if (seatRes?.success && seatRes?.data) {
        const { availableSeats, customerType } = seatRes.data;

        if (availableSeats > 0) {
          // Has seats: inform and proceed
          setSeatDialogTitle('Add User');
          setSeatDialogMessage(
            `You have ${availableSeats} seat${
              availableSeats === 1 ? '' : 's'
            } remaining. Adding a user will use 1 seat.`
          );
          setSeatDialogConfirmText('Continue');
          setSeatDialogAction('proceed');
          setSeatDialogOpen(true);
        } else if (availableSeats <= 0 && customerType === 'seat_based') {
          // No seats: fetch proration cost and offer purchase
          let prorationLabel = 'purchase an additional seat';
          try {
            const prorationRes = await getProrationPreview(global.me.orgID, 1);
            if (prorationRes?.success && prorationRes?.data) {
              prorationLabel = `purchase a seat for ${prorationRes.data.prorationAmountFormatted}`;
            }
          } catch (prorationErr) {
            console.error('Seat check: proration preview failed', prorationErr);
          }

          setSeatDialogTitle('No Seats Available');
          setSeatDialogMessage(
            `You've used all your seats. You'll need to ${prorationLabel} to add a new user. You'll be charged when the user is created.`
          );
          setSeatDialogConfirmText('Continue');
          setSeatDialogAction('purchase');
          setSeatDialogOpen(true);
        } else {
          // Unexpected state: just open the form
          navigate('/v2/people/new');
        }
      } else {
        // Seat check returned no data: proceed without blocking
        navigate('/v2/people/new');
      }
    } catch (err) {
      clearTimeout(timeout);
      setSeatCheckLoading(false);
      console.error('Seat check failed, proceeding with form', err);
      navigate('/v2/people/new');
    }
  };

  const handleSeatDialogConfirm = () => {
    setSeatDialogOpen(false);
    // Navigate with purchaseSeatOnCreate flag if needed
    if (seatDialogAction === 'purchase') {
      navigate('/v2/people/new', { state: { purchaseSeatOnCreate: true } });
    } else {
      navigate('/v2/people/new');
    }
  };

  const handleSeatDialogCancel = () => {
    setSeatDialogOpen(false);
    if (seatDialogAction === 'purchase') {
      toast.info(
        'Deactivate a user to free up a seat, or purchase additional seats in Settings.'
      );
    }
  };

  const handleUserClick = (userId: string) => {
    if (hasPermission(PermissionKeys.TeamsPeople_ViewUser, [userId])) {
      navigate(`/v2/people/${userId}`);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    try {
      const mutations = await import('../../../graphql/mutations');
      await API.graphql({
        query: mutations.personAdminFunctions,
        variables: {
          userID: '',
          username: '',
          users: JSON.stringify([deleteTarget]),
          action: 'delete',
        },
      });
      dispatch(removeUsersAction([deleteTarget.id]));
      dispatch(removeProfilesAction([deleteTarget.id]));
      toast.success('User deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('There was a problem deleting the user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getMemberType = (user: UserRow): string => {
    const type = user.orgs?.items?.[0]?.memberType || 'member';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const headerActions = [
    ...(isSuperAdmin() && featureFlag('bulk-imports')
      ? [
          {
            id: 'import',
            label: 'Import',
            variant: 'secondary' as const,
            onClick: () => navigate('/v2/people/import'),
          },
        ]
      : []),
    {
      id: 'add',
      label: seatCheckLoading ? 'Checking...' : 'New person',
      variant: 'primary' as const,
      onClick: handleAddPerson,
      disabled: seatCheckLoading,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="People"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
        actions={headerActions}
      />

      {/* Filters Bar */}
      <div className="px-4 sm:px-6 lg:px-10 py-4 bg-background space-y-3">
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
          <Input
            placeholder="Search"
            className="pl-11 bg-muted border-0 rounded-lg h-11 text-base font-medium"
            value={searchString}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Select value={roleFilter} onValueChange={handleRoleChange}>
            <SelectTrigger className="w-full bg-muted border-0 rounded-lg h-11 text-base font-medium gap-2">
              <div className="flex items-center gap-2">
                <UserCircle className="size-5 text-foreground shrink-0" />
                <SelectValue placeholder="All Roles" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="moderator">Moderator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full bg-muted border-0 rounded-lg h-11 text-base font-medium gap-2">
              <div className="flex items-center gap-2">
                <Zap className="size-5 text-foreground shrink-0" />
                <SelectValue placeholder="All Statuses" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>

          <Select defaultValue="50">
            <SelectTrigger className="w-full bg-muted border-0 rounded-lg h-11 text-base font-medium gap-2">
              <div className="flex items-center gap-2">
                <LayoutGrid className="size-5 text-foreground shrink-0" />
                <SelectValue placeholder="50 per page" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-auto px-6 lg:px-10 py-6 min-w-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <User className="size-12 mb-3" />
            <p className="text-sm">No people found.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              {/* Table Header */}
              <div className="grid grid-cols-[240px_1fr_1fr_1fr_1fr_1fr_1fr] min-w-[900px] bg-muted border-b border-border px-4 py-2.5">
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Project Name
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Email
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Phone
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Role
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Since
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize">
                  Status
                </span>
                <span className="text-sm font-bold text-muted-foreground capitalize text-center">
                  Actions
                </span>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="grid grid-cols-[240px_1fr_1fr_1fr_1fr_1fr_1fr] min-w-[900px] px-4 py-4 items-center hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleUserClick(user.id)}
                  >
                    {/* Name + Avatar */}
                    <div className="flex items-center gap-3 min-w-0">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="size-10 rounded-lg object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-10 rounded-lg bg-[hsl(194,85%,10%)] flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-white">
                            {user.initials}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-base font-medium text-foreground truncate">
                          {user.name}
                        </p>
                        {user.title && (
                          <p className="text-sm text-muted-foreground truncate">
                            {user.title}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <span className="text-sm font-medium text-muted-foreground truncate">
                      {user.email}
                    </span>

                    {/* Phone */}
                    <span className="text-sm font-medium text-muted-foreground truncate">
                      {user.phoneNumber}
                    </span>

                    {/* Role */}
                    <span className="text-sm font-medium text-muted-foreground">
                      {getMemberType(user)}
                    </span>

                    {/* Since */}
                    <span className="text-sm font-medium text-muted-foreground">
                      {formatDate(user.createdAt)}
                    </span>

                    {/* Status */}
                    <div>
                      <span
                        className={cn(
                          'inline-flex items-center justify-center text-sm font-bold uppercase px-3 py-2 rounded-lg border w-full',
                          user.status === 'active'
                            ? 'bg-muted border-border text-muted-foreground'
                            : 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                        )}
                      >
                        {user.status === 'active' ? 'ACTIVE' : 'DISABLED'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="size-10 rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="size-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/v2/people/edit/${user.id}`);
                            }}
                          >
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/v2/people/${user.id}`);
                            }}
                          >
                            View Profile
                          </DropdownMenuItem>
                          {hasPermission(
                            PermissionKeys.TeamsPeople_DeleteUser,
                            [user.id]
                          ) &&
                            user.id !== global.me.id && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(user);
                                }}
                              >
                                Delete
                              </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteUser}
        title="Remove Profile?"
        description="Are you sure you want to remove this profile, changes cannot be reversed"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Seat Check Dialog */}
      <ConfirmModal
        open={seatDialogOpen}
        onClose={handleSeatDialogCancel}
        onConfirm={handleSeatDialogConfirm}
        title={seatDialogTitle}
        description={seatDialogMessage}
        confirmLabel={seatDialogConfirmText}
        cancelLabel="Cancel"
        variant={seatDialogAction === 'purchase' ? 'warning' : 'info'}
      />
    </div>
  );
}
